/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiInputBase } from '@here/lui-base'
import scopedCss from 'lui-checkbox.scss'
import template from 'lui-checkbox.html'

export default class LuiCheckbox extends LuiInputBase {
  static get is() {
    return 'lui-checkbox'
  }

  static get attributeToggles() {
    return [
      'checked',
      'disabled',
      'indeterminate',
      'name',
      'required',
      'tabindex',
      'value',
    ]
  }

  static get clickTriggers() {
    return ['space']
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  constructor() {
    super()
    this.syncedProperties = ['checked', 'indeterminate', 'value', 'disabled']
  }

  init() {
    super.init()
    this._checkForLabels()
    if (!this.disabled) this.setAttribute('tabindex', 0)
  }

  onConnected() {
    super.onConnected()
    this._checkForLabels()
    this._onFieldClickBound = this._onFieldClickBound || ((e) => this._onFieldClick(e))
    this._onWrapperClickBound = this._onWrapperClickBound || ((e) => this._onWrapperClick(e))
    this.wrapper.addEventListener('click', this._onWrapperClickBound)
    this.delay(() => {
      this._getField()
      this.field.addEventListener('click', this._onFieldClickBound)
    })
  }

  onDisconnected() {
    super.onDisconnected()
    this.wrapper.removeEventListener('click', this._onWrapperClickBound)
    this.field.removeEventListener('click', this._onFieldClickBound)
  }

  _onFieldClick(e) {
    if (this.disabled) return
    if (this._ignoreChange) e.stopPropagation()
    this._readField()
  }

  _onWrapperClick() {
    if (this.hasAttribute('disabled')) return
    if (this._ignoreChange) return
    const event = new window.MouseEvent('click')
    this.field.dispatchEvent(event)
  }

  _checkForLabels() {
    this.label.style.display = this.hasChildNodes() ? 'block' : 'none'
  }

  _name() {
    let e
    return this.getAttribute('name') || ((e = this.querySelector('input')) && e.name)
  }

  _readField(field) {
    field = field || this.field
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(
        field))[property]
      const value = nativeImplementation ? field[property] : field.getAttribute(property)
      if (value) {
        this.setAttribute(property, value === true ? '' : value)
      } else {
        this.removeAttribute(property)
      }
    })
  }

  _writeField(field) {
    field = field || this.field
    this.syncedProperties.forEach((property) => {
      if (property !== 'disabled') {
        field[property] = this[property]
      }
    })
    const event = new Event('change')
    this.dispatchEvent(event)
  }

  disabledChanged() {
    super.disabledChanged()
    const disabled = this.disabled
    this.toggleCssClass('lui-disabled', disabled)
    this.wrapper.classList[disabled ? 'remove' : 'add']('lui-interactive')
  }

  checkedChanged() {
    this._writeField()
  }

  indeterminateChanged() {
    this._writeField()
  }

  valueChanged() {
    this._writeField()
  }

  _getField() {
    this._field = this.querySelector('input')
    this._ignoreChange = true
    const temporaryField = this.shadowRoot.querySelector('input')
    if (this._field) {
      if (temporaryField) temporaryField.parentNode.removeChild(temporaryField)
      if (this._field.type !== 'checkbox') {
        console.error(`<input type="${this._field.type}"> is not supported.`)
      }
      if (!this.fieldListenerInitialized) this._addPropertyListenerToField(this._field)
      this.fieldListenerInitialized = true
      this._readField(this._field)
      this._writeField(this._field)
    } else if (temporaryField) {
      this._field = temporaryField
      this._writeField(this._field)
    } else {
      const field = this._createField()
      this._writeField(field)
      this._field = field
    }
    this._ignoreChange = false
    return this._field
  }

  _createField() {
    const element = document.createElement('input')
    element.type = 'checkbox'
    this.label.appendChild(element)
    return element
  }

  _addPropertyListenerToField(field) {
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(
        field))[property]
      if (!nativeImplementation) return
      Object.defineProperty(field, property, {
        set: (value) => {
          nativeImplementation.set.call(field, value)
          if (this._ignoreChange) return
          this._ignoreChange = true
          this._readField()
          this._ignoreChange = false
        },
        get: nativeImplementation.get,
      })
    })
  }

  get label() {
    return this._label || (this._label = this.shadowRoot.querySelector('.lui-checkbox__label'))
  }

  get indicator() {
    return this._indicator || (this._indicator = this.shadowRoot.querySelector(
      '.lui-checkbox__indicator'))
  }

  get wrapper() {
    return this._wrapper || (this._wrapper = this.shadowRoot.querySelector(
      '.lui-checkbox__wrapper'))
  }

  get value() {
    return this._field ? this._field.value : this.getAttribute('value')
  }

  set value(newValue) {
    this.setAttribute('value', newValue)
    this.valueChanged('', newValue)
  }

  get checked() {
    return this._field ? this._field.checked : this.hasAttribute('checked')
  }

  set checked(newValue) {
    if (this.checked === newValue) return
    if (newValue) {
      this.setAttribute('checked', '')
    } else {
      this.removeAttribute('checked')
    }
  }

  get indeterminate() {
    return this._field ? this._field.indeterminate : this.hasAttribute('indeterminate')
  }

  set indeterminate(newValue) {
    if (this.indeterminate === newValue) return
    if (newValue) {
      this.setAttribute('indeterminate', '')
    } else {
      this.removeAttribute('indeterminate')
    }
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-checkbox/lui-checkbox.js
// module id = 185
// module chunks = 0 1 17